/// <reference types="google.maps" />
import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { AppStore } from 'src/app/core';
import { LocationGoogle, Offices, Place } from 'src/app/models/branches.model';
import { Loader } from '@googlemaps/js-api-loader';

@Component({
  selector: 'googlemaps-component',
  templateUrl: './googlemaps.component.html',
  styleUrls: ['./googlemaps.component.scss'],
  standalone: false
})
export class GooglemapsComponent implements OnInit, OnChanges {
  @ViewChild('map', { static: true }) mapElement!: ElementRef;
  map!: google.maps.Map;
  @Input() placeLists: Offices[] | undefined = [];
  @Input() idSelected: any;
  @Input() zoomTo = '';
  officesMapMarker: Place[] | [] = [];
  mapMarkerAsset = 'assets/images/map-marker-sucursal-default.svg';
  mapMarkerAssetActive = 'assets/images/map-marker-sucursal-active.svg';
  loading = false;
  targetPlace: Place | undefined;
  public latlngMapa: boolean;
  center: google.maps.LatLngLiteral = {
    lat: -33.4144234,
    lng: -70.5913315,
  };
  options: google.maps.MapOptions = {
    minZoom: 10,
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: true,
    fullscreenControl: true,
    streetViewControl: false,
    mapTypeControl: true,
    zoom: 8,
    center: this.center,
    gestureHandling: 'none',
  };
  locationInfo = '';
  apiLoaded: any;
  geocoder!: google.maps.Geocoder;
  loader!: Loader;

  constructor(private appStore: AppStore) {
    this.latlngMapa = false;
    this.targetPlace = undefined;
  }

  ngOnInit() {
    this.loader = new Loader({
      apiKey: this.appStore.env.googleMapsKey,
      version: 'weekly',
      ...this.options,
    });
    this.loader
      .load()
      .then((google) => {
        this.map = new google.maps.Map(
          this.mapElement.nativeElement,
          this.options
        );
        this.geocoder = new google.maps.Geocoder();
        this.getCurrentLocation();
        this.getOfficeForMaps();

        this.geocode({ address: this.zoomTo });
      })
      .catch((e) => {
        console.error('Cannot import google', e);
      });
  }

  geocode(request: google.maps.GeocoderRequest): void {
    if (!request.address) {
      return;
    }
    const cleanRequest = {
      address: request.address.replace('.', ' '),
      region: 'CL',
    };
    this.geocoder
      .geocode(cleanRequest)
      .then((result) => {
        const { results } = result;
        const location = {
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        };

        this.map.setCenter(location);
        this.map.setZoom(13);

        return results;
      })
      .catch((e) => {
        console.error(
          'Geocode was not successful for the following reason: ' + e
        );
      });
  }

  async ngOnChanges(changes: SimpleChanges) {
    this.getOfficeForMaps();
    if (changes['zoomTo']?.firstChange) {
      this.getCurrentLocation();
    }

    if (changes['placeLists']) {
      this.placeLists = changes['placeLists'].currentValue;
    }
    if (changes['idSelected']) {
      this.loader
        .load()
        .then(async (google) => {
          const { InfoWindow } = (await google.maps.importLibrary(
            'maps'
          )) as google.maps.MapsLibrary;
          if (this.idSelected) {
            this.targetPlace = this.officesMapMarker.find(
              (o: Place) => o.idOfi === this.idSelected
            );

            // Create an info window to share between markers.
            const infoWindow = new InfoWindow();
            const marker = new google.maps.Marker({
              title: this.targetPlace?.oficina,
              position: this.targetPlace?.ubicacion,
              map: this.map,
              ...this.targetPlace,
            });
            marker.setIcon(this.mapMarkerAssetActive);
            marker.setMap(this.map);
            // Add a click listener for each marker, and set up the info window.
            marker.addListener('click', () => {
              infoWindow.close();
              const url = `https://maps.google.com/?q=${this.targetPlace?.ubicacion?.lat},${this.targetPlace?.ubicacion?.lng}`;
              infoWindow.setContent(
                `${this.targetPlace?.oficina} </br> ${this.targetPlace?.direccion}
                </br> <a style="color: blue; font-family: Overpass, sans-serif;" target="_blank" href="${url}">
                Ver en google maps</a>`
              );
              infoWindow.open(this.map, marker);
            });

            const location = this.targetPlace?.ubicacion ?? this.center;

            this.map.setCenter(location);
            this.map.setZoom(18);
          }
        })
        .catch((e) => {
          alert('Google was not successful for the following reason: ' + e);
        });
    }
    if (changes['zoomTo']) {
      this.geocode({ address: this.zoomTo });
    }
  }
  centerInMap(center: google.maps.LatLngLiteral) {
    this.center = center;
    this.options = { ...this.options, center: this.center, zoom: 15 };
  }
  getOfficeForMaps() {
    this.officesMapMarker =
      this.placeLists !== undefined
        ? this.placeLists.map((place) => {
            const { ubicacion } = place;
            const officeMarker: Place = {
              options: {
                icon: this.mapMarkerAsset,
              },
              ...place,
              ubicacion: {
                lat: ubicacion?.latitud || 0,
                lng: ubicacion?.longitud || 0,
              },
            };
            const marker = new google.maps.Marker({
              position: {
                lat: ubicacion?.latitud || 0,
                lng: ubicacion?.longitud || 0,
              },
              map: this.map,
              icon: this.mapMarkerAsset,
              ...place,
            });
            marker.setMap(this.map);
            return officeMarker;
          })
        : [];
  }

  getCurrentLocation() {
    this.loading = true;
    try {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          const point: google.maps.LatLngLiteral = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          let locationGoogle: LocationGoogle = point;

          if (this.idSelected !== undefined) {
            locationGoogle = {
              lat: Number(this.targetPlace?.ubicacion.lat ?? 0),
              lng: Number(this.targetPlace?.ubicacion.lng ?? 0)
            };
          }

          this.map.setZoom(16);
          this.map.setCenter(locationGoogle);
        },
        (error) => {
          console.error(error);
          this.loading = false;
          if (error.PERMISSION_DENIED) {
            this.locationInfo = `Couldn't get your location, Permission denied`;
          } else if (error.POSITION_UNAVAILABLE) {
            this.locationInfo = `Couldn't get your location, Position unavailable`;
          } else if (error.TIMEOUT) {
            this.locationInfo = `Couldn't get your location, TimeOut`;
          } else {
            this.locationInfo = `Error: ${error.code}`;
          }
        },
        { enableHighAccuracy: true }
      );
    } finally {
      this.loading = false;
    }
  }
}
